/* eslint-disable react/jsx-props-no-spreading */
/* eslint-disable react/jsx-tag-spacing */
import { useEffect } from 'react'
import { Box, Alert } from '@mui/material'
import TableMui from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableFooter from '@mui/material/TableFooter'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import TableSortLabel from '@mui/material/TableSortLabel'
import { useTranslation } from 'react-i18next'
import {
  useGlobalFilter,
  usePagination,
  useSortBy,
  useTable,
  useFlexLayout,
  useAsyncDebounce
} from 'react-table'

import { enhancedTableStyles as styles } from 'src/components/shared/EnhancedTable/EnhancedTableStyles'
import { ArrowIcon } from 'src/components/shared/ui/Icons'
import Loader from 'src/components/shared/ui/Loader/Loader'
import { ToolTipHeader } from './EnhancedTable'
// import SimpleTablePagination from './SimpleTablePagination'
import TablePagination from './TablePagination'
import TableToolbar from './TableToolbar'
import { useRowStyles, useTableContainerStyles } from './useStyles'

export type SortBy = {
  id: string
  desc: boolean
}

type Props = {
  columns: any[]
  data: any[]
  title?: string
  onChangePaginationOptions: (value: {
    pageIndex: string
    pageSize: string
    sortBy: SortBy[]
    searchedText: string
  }) => void
  pageSize?: number
  pageCount: number
  totalRowsCount: number
  isFetching: boolean
  isError: boolean
  isSuccess: boolean
  onClickRow?: (row: any) => void
  useHeaderCellStyles?: () => any
  // simplePagination?: boolean
  sortable?: boolean
}

function AsyncEnhancedTable({
  columns,
  data,
  title,
  onChangePaginationOptions,
  pageSize: controlledPageSize,
  pageCount: controlledPageCount,
  totalRowsCount: controlledTotalRowsCount,
  isFetching,
  isError,
  isSuccess,
  onClickRow,
  // useHeaderCellStyles = () => null,
  // simplePagination = false,
  sortable = true
}: Props): JSX.Element {
  const containerClasses = useTableContainerStyles()
  // const headerClasses = useHeaderCellStyles()
  // const defaultHeaderClases = useDefaultHeaderCellStyles()
  const rowClasses = useRowStyles()
  const {
    getTableProps,
    headerGroups,
    prepareRow,
    page,
    gotoPage,
    setPageSize,
    setGlobalFilter,
    state
  } = useTable(
    {
      columns,
      data,
      initialState: { pageIndex: 0, pageSize: controlledPageSize },
      manualPagination: true,
      manualSortBy: true,
      disableSortBy: !sortable,
      disableMultiSort: true,
      pageCount: controlledPageCount,
      manualGlobalFilter: true
    },
    useGlobalFilter,
    useSortBy,
    usePagination,
    useFlexLayout
  )
  const { pageIndex, pageSize, globalFilter, sortBy } = state
  const onChangePaginationOptionsDebounced = useAsyncDebounce(onChangePaginationOptions, 100)
  const { t } = useTranslation()

  useEffect(() => {
    onChangePaginationOptionsDebounced({ pageIndex, pageSize, sortBy, searchedText: globalFilter })
  }, [
    onChangePaginationOptions,
    pageIndex,
    pageSize,
    sortBy,
    globalFilter,
    onChangePaginationOptionsDebounced
  ])

  const handleChangePage = (event, newPage) => {
    gotoPage(newPage)
  }

  const handleChangeRowsPerPage = event => {
    setPageSize(Number(event.target.value))
  }

  return (
    <TableContainer classes={containerClasses}>
      <TableToolbar title={title} setSearchText={setGlobalFilter} searchText={globalFilter} />
      <TableMui {...getTableProps()} sx={styles.tableMui}>
        <TableHead>
          {headerGroups.map(headerGroup => (
            <TableRow {...headerGroup.getHeaderGroupProps()}>
              {headerGroup.headers.map(column => {
                return (
                  <TableCell
                    sx={styles.headerCell}
                    {...column.getHeaderProps(column.getSortByToggleProps())}
                  >
                    <Box sx={styles.headerCellText} flex="0 1 auto">
                      <ToolTipHeader column={column} />
                    </Box>
                    {sortable ? (
                      <TableSortLabel
                        sx={styles.headerCellSort}
                        active={column.isSorted}
                        // react-table has a unsorted state which is not treated here
                        direction={column.isSortedDesc ? 'desc' : 'asc'}
                        IconComponent={() => (
                          <ArrowIcon
                            sx={{
                              transform: `rotate(${column.isSortedDesc ? '180deg' : '0deg'})`
                            }}
                          />
                        )}
                      />
                    ) : null}
                  </TableCell>
                )
              })}
            </TableRow>
          ))}
        </TableHead>
        <TableBody>
          {page.map((row, _i) => {
            prepareRow(row)
            return (
              <TableRow
                classes={rowClasses}
                {...row.getRowProps()}
                onClick={onClickRow ? () => onClickRow(row) : undefined}
              >
                {row.cells.map(cell => {
                  return (
                    <TableCell sx={styles.bodyCell} {...cell.getCellProps()}>
                      {cell.render('Cell')}
                    </TableCell>
                  )
                })}
              </TableRow>
            )
          })}
          {isSuccess && data.length === 0 && (
            <Box
              component="tr"
              display="flex"
              alignItems="center"
              justifyContent="center"
              padding={2}
              minHeight={80}
              className={rowClasses.noResults}
            >
              <td>{t('components.enhanced_table.no_registers.default')}</td>
            </Box>
          )}
        </TableBody>
        <TableFooter sx={styles.footer}>
          {isSuccess && (
            <TableRow>
              {/* {simplePagination ? (
                <SimpleTablePagination
                  colSpan={3}
                  count={controlledTotalRowsCount}
                  page={pageIndex}
                  onChangePage={handleChangePage}
                  rowsPerPage={pageSize}
                />
              ) : ( */}
              <TablePagination
                rowsPerPageOptions={[5, 10, 25, 50]}
                colSpan={3}
                count={controlledTotalRowsCount}
                labelRowsPerPage="Registros por pág."
                rowsPerPage={pageSize}
                page={pageIndex}
                SelectProps={{
                  inputProps: { 'aria-label': 'registros por página' },
                  native: false
                }}
                onChangePage={handleChangePage}
                onChangeRowsPerPage={handleChangeRowsPerPage}
              />
              {/* )} */}
            </TableRow>
          )}
        </TableFooter>
      </TableMui>
      {isFetching && (
        <Box display="flex" alignItems="center" justifyContent="center" padding={2}>
          <Loader />
        </Box>
      )}
      {isError && (
        <Box display="flex" alignItems="center" justifyContent="center" padding={2}>
          <Alert severity="error">Se ha producido un error recuperando la información</Alert>
        </Box>
      )}
    </TableContainer>
  )
}

export default AsyncEnhancedTable
